import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Sentry } from '../lib/sentry';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Sentry.captureException is a no-op when Sentry wasn't initialized,
    // so this is safe even in dev/CI without a DSN.
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
  }

  render() {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <main className="container error-boundary">
          <h1>Something went wrong.</h1>
          <p className="muted">
            The error has been recorded. Try refreshing the page; if it keeps
            happening, contact support with the time you saw it.
          </p>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => { this.setState({ error: null }); window.location.reload(); }}
          >
            Reload
          </button>
        </main>
      );
    }
    return this.props.children;
  }
}
